import classNames from 'classnames';
import { CookiesBanner } from 'components/CookiesBanner/CookiesBanner';
import wcagBadge from 'icons/wcag.svg';
import type { Recipient } from 'models/recipient';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { RootState } from 'reducers/types';
import { isChatVisible } from 'selectors/ui/ui';

import { AddressBuilder } from './AddressBuilder';
import { Powered } from './Powered/Powered';

import './Footer.scss';

type RecipientWithWebsite = Pick<
  Recipient,
  'name' | 'address' | 'address2' | 'state' | 'city' | 'zip'
> & { websiteUrl?: string; websiteHypertext?: string };

const renderAddress = (
  {
    name,
    address,
    address2,
    state,
    city,
    zip,
    websiteUrl,
    websiteHypertext,
  }: RecipientWithWebsite,
  trackNobookingCta?: () => void,
) => {
  return new AddressBuilder()
    .basicAddress(
      name,
      websiteUrl,
      websiteHypertext,
      address,
      trackNobookingCta,
    )
    .add(address2)
    .add(city)
    .add(state)
    .add(zip)
    .render();
};

type FooterProps = ConnectedProps<typeof connector> & {
  footer?: boolean;
  isNonClient?: boolean;
  recipient?: RecipientWithWebsite;
  showAddress?: boolean;
  trackNobookingCta?: () => void;
};

const FooterComponent = ({
  footer = true,
  isNonClient = false,
  isChatVisible,
  recipient,
  showAddress = false,
  trackNobookingCta,
}: FooterProps) => {
  const contentClassName = classNames('Footer-content', {
    'with-chat': isChatVisible,
  });

  return footer ? (
    <>
      <footer className="Footer" data-testid="footer">
        <div className={contentClassName}>
          {showAddress && recipient && (
            <div className="Footer-address">
              {renderAddress(recipient, trackNobookingCta)}
            </div>
          )}
          <div className="Footer-copyright">
            {!isNonClient && <Powered />}
            <p className="Footer-copyrightMessage">
              {isNonClient ? (
                <>
                  © Cohort Go 2012-26 ABN 63 154 889 139, AFS License Number
                  455592 NZBN 5857910, NZ Financial Service Provider FSP 488926
                </>
              ) : (
                <>
                  Copyright ©Flywire. 2009-{new Date().getFullYear()} All rights
                  reserved.
                  <br />
                  Flywire is a trademark of Flywire Corporation.
                </>
              )}
            </p>
            <a
              href="https://certifications.onlineada.com/flywire-payex-q2-2024-certification"
              rel="noopener noreferrer"
              target="_blank"
            >
              <img
                src={wcagBadge}
                alt="Certified WCAG 2.1 AA Conformant"
                className="Footer-certification"
              />
            </a>
          </div>
        </div>
      </footer>
      <CookiesBanner />
    </>
  ) : null;
};

const mapStateToProps = (state: RootState) => ({
  isChatVisible: isChatVisible(state),
});

const connector = connect(mapStateToProps);

const Footer = connector(FooterComponent);

export { Footer };
